import { RotateCcw, MoreHorizontal, Building2, FileText, Clipboard, Link2, ExternalLink, Loader2, Check, Zap } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui'
import { useROIStore } from '../store'
import { useHomeStore } from '@/features/home/store'
import { roiApi, draftsApi } from '@api/client'
import { generateTextSummary, exportROIToPDF } from '../utils/exportUtils'
import { showToast } from '@store/uiStore'

export function ROIHeader() {
  const { regions, aggregatedResults, getRegionCalculation, reset, isCustomerView, getROIState } = useROIStore()
  const { selectedDeal } = useHomeStore()
  const [searchParams] = useSearchParams()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [copiedText, setCopiedText] = useState(false)
  const [copiedLink, setCopiedLink] = useState<'customer' | 'internal' | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const sessionDealId = searchParams.get('session')
  const draftId = searchParams.get('draft')
  const customerName = selectedDeal?.companyName || selectedDeal?.name || ''

  const handleConvertDraft = async () => {
    if (!draftId || draftId === 'new') return
    setIsConverting(true)
    try {
      const res = await draftsApi.convert(Number(draftId))
      if (res.success && res.data) {
        navigate(`/roi?session=${res.data.dealId}`)
      } else {
        showToast(res.error || 'Failed to convert draft to session', 'error')
      }
    } catch (error) {
      console.error('Convert draft error:', error)
      showToast('Failed to convert draft. Please try again.', 'error')
    } finally {
      setIsConverting(false)
    }
  }

  const handleReset = () => {
    if (confirm('Reset all regions to default values?')) {
      reset()
    }
    setActionsOpen(false)
  }

  // Save ROI state to DB and return the deal ID
  const saveROIState = async (): Promise<string | null> => {
    const dealId = sessionDealId || selectedDeal?.id
    if (!dealId) {
      showToast('Please link a deal before sharing. Go to Home and select a HubSpot deal first.', 'warning')
      return null
    }

    setIsSaving(true)
    try {
      const roiState = getROIState()

      // Try to save via the ROI-specific endpoint
      // First check if an ROI record exists for this deal
      const existingResponse = await roiApi.getByDealId(Number(dealId))

      if (existingResponse.success && existingResponse.data) {
        // Update existing
        await roiApi.update(existingResponse.data.id, {
          inputs_json: JSON.stringify(roiState),
          outputs_json: aggregatedResults ? JSON.stringify(aggregatedResults) : undefined,
          customer_name: customerName,
        } as any)
      } else {
        // Create new
        await roiApi.create({
          deal_id: Number(dealId),
          customer_name: customerName,
          inputs_json: JSON.stringify(roiState),
          outputs_json: aggregatedResults ? JSON.stringify(aggregatedResults) : undefined,
          status: 'draft',
        } as any)
      }

      return dealId
    } catch (error) {
      console.error('Failed to save ROI state:', error)
      showToast('Failed to save ROI data. Please try again.', 'error')
      return null
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopyTextSummary = async () => {
    const summary = generateTextSummary({
      regions,
      aggregatedResults,
      getRegionCalculation,
      customerName,
    })

    try {
      await navigator.clipboard.writeText(summary)
      setCopiedText(true)
      setTimeout(() => setCopiedText(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = summary
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedText(true)
      setTimeout(() => setCopiedText(false), 2000)
    }

    setActionsOpen(false)
  }

  // Generate URL-encoded customer link (no DB required, like original calculator)
  const generateCustomerLink = (): string => {
    const params = new URLSearchParams()

    const regionsData = regions.map(r => ({
      n: r.name || '',
      c: r.country,
      i: r.industry,
      p: r.provider,
      t: r.product,
      r: r.passRate,
      v: r.volume,
      val: r.valuePerCustomer,
      s: r.stack.map(s => s.profile).join('|'),
    }))

    params.set('regions', JSON.stringify(regionsData))
    params.set('view', 'customer')

    return `${window.location.origin}/roi/share?${params.toString()}`
  }

  const handleCopyLink = async (mode: 'customer' | 'internal') => {
    let url: string

    if (mode === 'customer') {
      // Customer link: encode state directly in URL (no DB needed)
      url = generateCustomerLink()
    } else {
      // Internal link: save to DB and use session ID
      const dealId = await saveROIState()
      if (!dealId) return
      url = `${window.location.origin}/roi?session=${dealId}`
    }

    try {
      await navigator.clipboard.writeText(url)
      setCopiedLink(mode)
      setTimeout(() => setCopiedLink(null), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedLink(mode)
      setTimeout(() => setCopiedLink(null), 2000)
    }

    setActionsOpen(false)
  }

  const handleExportPDF = () => {
    exportROIToPDF({
      regions,
      aggregatedResults,
      getRegionCalculation,
      customerName,
    })
    setActionsOpen(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActionsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Customer view: clean presentation header
  if (isCustomerView) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">ROI Analysis</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            The value FrankieOne delivers by improving pass rates
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">ROI Calculator</h1>
          <p className="text-sm text-[var(--text-muted)]">
            Calculate the value FrankieOne delivers<br />by improving pass rates
          </p>
        </div>

        {/* Center - Deal Tag */}
        <div className="flex-1 flex justify-center">
          {selectedDeal ? (
            <Link
              to="/home"
              className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/30 rounded-full text-xs text-primary hover:bg-primary/20 transition-colors"
            >
              <Building2 size={12} />
              <span className="font-medium">{selectedDeal.name}</span>
              <span className="text-primary/60">#{selectedDeal.hubspotDealId || selectedDeal.id}</span>
            </Link>
          ) : draftId ? (
            <button
              onClick={handleConvertDraft}
              disabled={isConverting || draftId === 'new'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-full text-xs text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
            >
              {isConverting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Zap size={12} />
              )}
              <span className="font-medium">{isConverting ? 'Creating session...' : 'Start a Session'}</span>
            </button>
          ) : (
            <Link
              to="/home"
              className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-xs text-amber-400 hover:bg-amber-500/20 transition-colors"
            >
              <Building2 size={12} />
              <span>No deal linked — your edits will be lost if not saved</span>
            </Link>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Reset Button */}
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <RotateCcw size={14} className="mr-1.5" />
            Reset
          </Button>

          {/* Actions Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <Button variant="primary" size="sm" onClick={() => setActionsOpen(!actionsOpen)}>
              <MoreHorizontal size={14} className="mr-1.5" />
              Actions
            </Button>

            {actionsOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-dropdown z-50">
                <div className="py-1">
                  {/* Share Section */}
                  <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                    Share
                  </div>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                    onClick={() => handleCopyLink('customer')}
                  >
                    {copiedLink === 'customer' ? (
                      <Check size={14} className="text-success" />
                    ) : (
                      <ExternalLink size={14} className="text-primary" />
                    )}
                    {copiedLink === 'customer' ? 'Link Copied!' : 'Copy Customer Link'}
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                    onClick={() => handleCopyLink('internal')}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <Loader2 size={14} className="text-primary animate-spin" />
                    ) : copiedLink === 'internal' ? (
                      <Check size={14} className="text-success" />
                    ) : (
                      <Link2 size={14} className="text-[var(--text-muted)]" />
                    )}
                    {copiedLink === 'internal' ? 'Link Copied!' : 'Copy Internal Link'}
                  </button>

                  <div className="border-t border-[var(--border-color)] my-1" />

                  {/* Export Section */}
                  <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                    Export
                  </div>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                    onClick={handleCopyTextSummary}
                  >
                    {copiedText ? (
                      <Check size={14} className="text-success" />
                    ) : (
                      <Clipboard size={14} className="text-[var(--text-muted)]" />
                    )}
                    {copiedText ? 'Copied to Clipboard!' : 'Copy Text Summary'}
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                    onClick={handleExportPDF}
                  >
                    <FileText size={14} className="text-primary" />
                    Export PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
